import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Close, Send } from "@mui/icons-material";
import invoiceService from "../../services/invoiceService";

// Shows exactly what createInvoice would produce — same rendered HTML the
// customer/print pages use — WITHOUT creating anything, so the admin can
// review the real invoice document before committing to it. `payload` is
// the same shape AdminGenerateInvoice/InvoiceDialog already send to
// invoiceService.create (customer/customerEmail, items, lines, dueDate,
// notes). "Edit" just closes this and returns to the form as-is — nothing
// here is destructive until "Generate and Send Invoice" is clicked.
const InvoicePreviewDialog = ({ open, onClose, onEdit, payload, onConfirm, confirming }) => {
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !payload) return;
    setHtml(null);
    setError("");
    setLoading(true);
    invoiceService
      .preview(payload)
      .then(({ data }) => setHtml(data.data.html))
      .catch((err) => setError(err.response?.data?.message || "Failed to build invoice preview"))
      .finally(() => setLoading(false));
  }, [open, payload]);

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: "90vh" } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography fontWeight={700}>Invoice Preview</Typography>
        <IconButton size="small" onClick={onClose} disabled={confirming}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowY: "auto", bgcolor: "action.hover" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {/* Always white page / dark text — this previews the real invoice
            document, which is meant to be printed/emailed and always
            assumes a white backdrop of its own (same as the admin's
            Template editor previews and the print pages), regardless of
            the app's own light/dark mode. */}
        {html && (
          <Box
            sx={{ maxWidth: 800, mx: "auto", bgcolor: "#fff", color: "#0E1420", boxShadow: 1 }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onEdit} disabled={confirming}>
          Edit
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <Send />}
          onClick={onConfirm}
          disabled={loading || !!error || confirming}
        >
          Generate and Send Invoice
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePreviewDialog;
